import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from '@reach/router';

import LegalDocument from '../../components/LegalDocument';

function TermsEn() {
  return (
    <LegalDocument title="Terms Of Service">
      <Helmet>
        <meta name="description" content="EFFECTIVE DATE: June 1, 2018" />
        <meta itemprop="description" content="EFFECTIVE DATE: June 1, 2018" />
        <meta
          property="og:description"
          content="EFFECTIVE DATE: June 1, 2018"
        />
      </Helmet>
      <p>EFFECTIVE DATE: September 1, 2023</p>
      <h4>Thank you for choosing Spur.</h4>
      <p>
        These terms and conditions, along with our privacy policy available at{' '}
        <Link to="/privacy-policy">“Privacy Policy”</Link>, and all applicable
        laws and conditions or policies referenced herein, which are
        incorporated herein by reference (collectively the “Terms of Service”)
        govern your use of this Website (
        <a href="https://spurjobs.com/">www.SpurJobs.com</a>) or our mobile
        applications (in either case, the “Site”). By using the Site, you accept
        the Terms of Service in full. If you disagree with any part of the Terms
        of Service, you must not use the Site. You must be competent to enter
        into a contract under the law of your state of residence to use the
        Site. By using the Site and by agreeing to the Terms of Service, you
        warrant and represent that you have reached the age of majority and are
        competent to enter into a contract under applicable federal, state and
        local laws. By accessing, registering with, or using the Site in any
        manner, including, but not limited to, visiting or browsing the Site or
        contributing content, information, or other materials or services to the
        Site, you agree to be bound by the Terms of Service. Your use of this
        Site must be in strict compliance with these Terms of Service and
        consistent with all applicable local, state, national, and international
        laws and regulations. YOU MAY NOT USE THE SITE IF YOU DO NOT AGREE TO
        THESE TERMS OF SERVICE.
      </p>
      <p>
        This Site uses cookies. By using the Site and agreeing to the Terms of
        Service, you consent to Spur’s use of cookies in accordance with the
        terms of Spur’s Privacy Policy.
      </p>
      <h2>Table of Contents</h2>
      <ol>
        <li>Scope of Use</li>
        <li>Registration, Access, and Security</li>
        <li>Terms Specific to Workers</li>
        <li>Terms Specific to Workplaces</li>
        <li>
          User Information, user Generated Content, and Restrictions on Use
        </li>
        <li>Privacy Policy</li>
        <li>No Warranties</li>
        <li>Trademarks</li>
        <li>Copyright Infringement Notice</li>
        <li>Reviews, Comments, and Other Content</li>
        <li>Modifications</li>
        <li>Violation of Rules and Regulations; Disclosure of Information</li>
        <li>Indemnity</li>
        <li>Limitation of Liability</li>
        <li>Third Party Sites</li>
        <li>No Fiduciary Relationship</li>
        <li>Right to Monitor</li>
        <li>Electronic Communications</li>
        <li>Governing Law and General Principles</li>
        <li>Arbitration and Class Action</li>
        <li>WAIVER OF JURY TRIAL</li>
        <li>Jurisdiction and Venue</li>
      </ol>
      <h3>Scope of Use</h3>
      <p>
        Spur, Inc. and its affiliates, subsidiaries, agents, representatives,
        successors and assigns (collectively <strong>“Spur”</strong>,{' '}
        <strong>“we”</strong>, <strong>“us”</strong>, or <strong>“our”</strong>)
        provide the Site to you, the user of the Site (<strong>“you”</strong> or{' '}
        <strong>“your”</strong>) for your informational, noncommercial use, and
        subject to the Terms of Service. The use of this Site to evaluate
        whether to enter into a business relationship with Spur shall not
        constitute a commercial use of the Site. It is a violation of the Terms
        of Service for you to use the Site in violation of any laws and
        regulations or in violation of the rules of any Spur service providers.
        Certain other programs or services provided by Spur through linked
        websites or other channels may have additional terms and conditions that
        govern your use of those services, and nothing in these Terms of Service
        is intended to modify, limit, or otherwise impair such terms of service.
      </p>
      <p>
        Spur is a staffing services company in the business of applying its
        professional judgment to provide tools and other resources to determine
        the optimal pairings of service workers and those in need of related
        services. The Site is a web-based communications platform which enables
        Workers and Workplaces to connect. <strong>“Workplaces”</strong> are
        individuals or business entities who seek to obtain temporary services (
        <strong>“Jobs”</strong>) from Workers through Spur.{' '}
        <strong>“Workers”</strong> are individuals employed by Spur for the
        purpose of performing one or more Jobs for one or more Workplaces.
        Workers and Workplaces together are hereinafter referred to as{' '}
        <strong>“users</strong>.”
      </p>
      <p>
        As described later in these Terms of Service, when a Worker is engaged
        through Spur to perform a Job for a Workplace, the Worker shall at all
        times during such Job be an employee of Spur and shall not be an
        employee of the Workplace. The Workplace may not hire or terminate any
        Spur employee. Spur reserves the sole right to hire and fire Workers.
        The foregoing shall not limit the Workplace’s right, as herein provided,
        to terminate any Job or request that Spur provide a replacement Worker
        for a Job. In the event Spur terminates a Worker during a Job, Spur
        shall use its reasonable best efforts to tender to the Workplace a
        replacement Worker as soon as practical, at which point, unless Spur has
        directed otherwise, the replacement Worker will need to be engaged by
        the Workplace using the same process as used for a new Job as described
        below.
      </p>
      <p>
        Due to the nature of the Internet, Spur cannot guarantee the continuous
        and uninterrupted availability and accessibility of the Site. We may
        restrict the availability of the Site or certain areas or features
        thereof, in our sole discretion, including where we have determined that
        such restrictions are necessary to address capacity limits, the security
        or integrity of our servers, to carry out maintenance measures that
        ensure the proper or improved functioning of the Site or our required
        compliance with any law, regulation, or governmental agency mandate. The
        Site may also be affected by activities or events beyond our control,
        such as internet service provider outages or problems, fire, war,
        accident, act of God or act of terrorism. In addition, we may improve,
        enhance or modify the Site and introduce new services from time to time.
      </p>
      <h3>Registration, Access, and Security</h3>
      <p>
        If you use any Spur service and provide us any information through the
        Site, such as your name, social security number, previous work
        experience, education background, birthdate, criminal history, bank
        account information, credit card information, federal employment
        identification number, citizenship status, address, zip code, phone
        number, contact details, email address, and/or Your password
        (collectively, Your <strong>“User Information”</strong>) for any
        accounts associated with you (<strong>“Your Account(s)”</strong>), you
        agree to provide true, accurate, current, complete and up-to-date
        information. If you provide any information that is untrue, inaccurate,
        non-current or incomplete, or we have reasonable grounds to suspect that
        such information is untrue, inaccurate, non-current or incomplete, then
        we have the right to terminate or refuse any and all current or future
        access or use of the Site (or any portion thereof).
      </p>
      <p>
        We reserve the right to take any action that we deem necessary to ensure
        the security of the Site and Your Account, including without limitation
        changing your password, terminating Your Account, or requesting
        additional information to authorize transactions on Your Account. You
        are solely responsible for keeping Your User Information and any
        security questions and responses associated with Your Account
        confidential. Anyone with knowledge of or access to Your User
        Information or the security questions and responses associated with Your
        Account can use that information to gain access to Your Account.
      </p>
      <p>
        You are solely liable for any claims, damages, losses, costs or other
        liabilities resulting from or caused by any failure to keep Your User
        Information and the security questions and responses confidential,
        whether such failure occurs with or without your knowledge or consent.
        You must immediately notify us of any suspected or actual unauthorized
        access to or use of Your Account, your User Information, or any other
        breach of Your Account security. You hereby authorize and consent to the
        collection, storage, sharing, use, transfer and sale by Spur and its
        service providers, of any information and data related to or derived
        from your use of the Site.
      </p>
      <p>
        You are responsible for providing and maintaining, at your own risk,
        option and expense, any hardware, software and communication lines
        required to access and use the Site. Spur reserves the right to change
        the access configuration of the Site at any time, without prior notice.
      </p>
      <h3>Terms Specific to Workers</h3>
      <p>
        NOTHING IN THESE TERMS OF SERVICE IS INTENDED TO CREATE AN EMPLOYMENT
        CONTRACT. YOU WILL BE AN EMPLOYEE “AT WILL” OF SPUR, WHICH MEANS THAT
        THE EMPLOYMENT RELATIONSHIP WITH SPUR MAY BE TERMINATED BY YOU OR BY US
        AT ANY TIME, WITH OR WITHOUT CAUSE, AND WITHOUT NOTICE. JOBS ARE
        ACCEPTED WITH THE UNDERSTANDING THAT NO GUARANTEE OR PROMISE OF DURATION
        OF ASSIGNMENT IS GIVEN OR IMPLIED.
      </p>
      <p>
        You acknowledge and agree that the Site is intended for use only by
        Workers who are able to enter into an employment relationship with Spur
        and, as an employee of Spur, to perform Jobs for Workplaces that engage
        the Worker through the Site. Your status as an employee of Spur is
        expressly contingent upon you not having any binding employment
        agreements from previous employers or other legal restrictions that
        would prevent you from becoming a Worker or otherwise accepting a Job
        with a Workplace, your proof of right to work in the United States, and
        our completion of a successful background investigation, which are
        generally completed prior to you being hired as an employee of Spur.
      </p>
      <p>
        To qualify for certain Jobs, you may be required to successfully
        complete a more in-depth background investigation than the investigation
        we complete at your time of initial hire by Spur, or you may be required
        to provide the results of a negative drug screening or proof that you
        possess a specialized license, have undergone certain training, or hold
        other certifications, or you may be required to agree to certain
        confidentiality, non-solicitation, works-made-for-hire, or other
        restrictive covenant agreements with the Workplace. If you choose to
        apply for these Jobs, you are responsible for satisfying these
        additional requirements at your sole cost. Spur will not be responsible
        for any related expenses.
      </p>
      <h4>Our Relationship:</h4>
      <p>
        By accepting these Terms of Service, you agree that you are an at-will
        employee of Spur. During any Job with a Workplace, your relationship
        with that Workplace will be as a placed Worker of Spur (a{' '}
        <strong>“Placed Worker”</strong>), and not as an employee of the
        Workplace. As a result, you will not be entitled to any rights of an
        employee with respect to any Workplace.
      </p>
      <p>
        You may not subcontract any Job, without the prior written consent of
        Spur and the Workplace that posted the Job.
      </p>
      <p>As an employee of Spur, you acknowledge and agree to:</p>
      <ul>
        <li>
          complete any mandatory training that we may require from time to time,
        </li>
        <li>
          comply with all applicable work rules, dress codes, code of conduct,
          security measures, and other policies set forth in the Spur Employee
          Handbook or other policies adopted by Spur, which may be amended from
          time to time and will be made available to you on the Site,
        </li>
        <li>
          contact Spur regarding any complaints or concerns that you may have
          about a Job or Workplace or the actions or inactions of Workplace or
          Spur personnel, and
        </li>
        <li>
          provide services to any Workplace only pursuant to the Job that you
          have accepted and not to perform additional services for any such
          Workplace unless the prior written approval of Spur has been obtained
          or a new Job has been posted to our Site and properly accepted by you.
        </li>
      </ul>
      <h4>Employee Compensation and Benefits:</h4>
      <p>
        While the Workplace sets the actual hourly rate paid for a particular
        Job, Spur reserves the sole right to establish the total fees to be paid
        by a Workplace to Spur wages in connection with any Job, and assumes
        responsibility for deducting Spur’s fees and then paying the resulting
        wages to you when due, and withholding and depositing all unemployment
        compensation, income tax withholding and other payroll taxes and other
        withholdings from such amounts as required by law or contract. As your
        employer, Spur has the sole discretion to determine whether any employee
        benefits will be offered to you and the terms thereof. Any such employee
        benefits will at all times be subject to the terms and conditions set
        forth in the governing documents for such benefit, which may be modified
        or terminated, in our sole discretion, at any time. If you work enough
        hours through Jobs to be considered a “full-time employee” of Spur for
        purposes of the Affordable Care Act, Spur will be responsible for
        offering qualifying medical coverage to you.
      </p>
      <p>
        You will not be entitled at any time to any benefits or perquisites
        offered by a Workplace, except where specifically agreed in writing
        between Spur and the Workplace in which event only the Workplace and not
        Spur shall be liable for the provision of the agreed-upon benefits or
        perquisites.
      </p>
      <h4>Liability Insurance:</h4>
      <p>
        Spur is responsible for maintaining workers’ compensation insurance for
        you, as required by law. We also purchase general liability,
        professional, and employment practices insurance that offers protection
        to us and to our workforce from certain claims.
      </p>
      <h4>Restrictive Covenants/Confidentiality:</h4>
      <p>
        You hereby represent, covenant, and warrant that you are not covered by
        any restrictive covenant (<strong>“restrictive covenant”</strong>),
        including, but not limited to, a non-competition, non-solicitation, or
        non-disclosure agreement, that would prohibit your employment by Spur.
        You agree that you will not accept any Job that violates any restrictive
        covenant to which you are now, or may become, subject. You recognize
        that we do not have the ability and obligation to pre-screen or validate
        any information regarding any restrictive covenant to which you may be
        subject now or in the future, and that the compliance with any such
        restrictive covenant is your sole responsibility.
      </p>
      <p>
        You recognize that during the course of your employment with Spur, you
        may be exposed or have access to, or Spur or a Workplace may disclose or
        provide to you, Trade Secrets and Confidential Information. The Trade
        Secrets and Confidential Information of Spur and each Workplace are the
        sole and exclusive property of Spur or such Workplace (as applicable).
        The disclosure of any Trade Secrets and Confidential Information to you
        shall not give you any license, interest or rights of any kind in or to
        the Trade Secrets or Confidential Information. You agree that all
        confidential information that you obtain from or about a Workplace while
        responding to or during a Job should not be communicated or otherwise
        shared with anyone, other than authorized personnel of that Workplace.
        In addition, your rate of pay and benefits with Spur are deemed
        confidential matters between you and Spur, and absent a compelling
        reason to the contrary, are not to be disclosed to third parties,
        including the personnel of any Workplace.
      </p>
      <p>
        You agree to indemnify, defend, and hold Spur and each Workplace
        harmless from any suit, claim, or other expense arising out of or
        related to your violation or alleged violation of such restrictive
        covenant or the above confidentiality restrictions.
      </p>
      <h4>Job Posts, Frequency, &amp; Pay Rates:</h4>
      <p>
        You acknowledge and agree that Workplaces are solely responsible for
        posting Jobs on the Site. Spur is not obligated to monitor the posting
        of Jobs, the source of Jobs, the number of Jobs posted, or their
        frequency. You agree that Workplaces solely control the posting and
        availability of Jobs in their discretion. Spur does not guarantee that
        any amount of Jobs will be made available to you through the Site, as
        availability is fully dependent on Workplace demand, which may drop off
        at any time for any reason.
      </p>
      <p>
        The Workplace sets the hourly pay rate for Jobs booked through Spur. You
        acknowledge and agree that Spur is not involved in setting any of these
        rates, other than requiring that the Workplace satisfy the hourly
        minimum wage rates and overtime required by applicable federal, state or
        local law. Employees are entitled to overtime pay under the specific
        provisions of federal law at a rate of 1 and ½ times the employee’s
        hourly rate at which the overtime occurred for time worked in excess of
        40 hours per work week. We will not be held responsible for any issues
        or disputes that arise with respect to the Jobs posted on the Site.
        Additionally, Spur is not obligated to review or overturn a Workplace’s
        decision to reject a Worker’s offer to accept a Job or a Workplace’s
        rejection of a Worker, whether due to nonperformance, unsatisfactory
        performance, or other reasons, and Spur will not be held responsible for
        any such Workplace decisions.
      </p>
      <p>
        Unless specifically provided in a Job posting or required by law, no
        special pay rate will be due if a Job is performed on a holiday.
      </p>
      <p>
        You acknowledge and agree that Spur limits the number of hours that you
        may work in a day or work week. Our work day begins at 12:01 a.m. and
        ends at 12:00 midnight, and our work week begins at 12:01 a.m. each
        Monday and ends at 12:00 midnight on the following Sunday, in the time
        zone in which the Job is being performed (or, if performed virtually or
        remotely, in the location of the Workplace’s headquarters). We currently
        limit you to thirty-eight (38) hours per work week.
      </p>
      <p>
        You are not permitted to work beyond the hours stated in the Job that
        you have accepted on our Site.
      </p>
      <h4>Background Checks and Authorization and Release:</h4>
      <ul>
        <li>
          Background Checks: We or our designated agents or representatives may
          request background information about you from a consumer reporting
          agency in connection with your request to use the Site for work
          purposes and/or your submission for certain Jobs through the Site. The
          Fair Credit Reporting Act (the <strong>“FCRA”</strong>) defines such a
          report, including investigative consumer reports as defined under the
          FCRA, as a “Consumer Report”, and all inquiries are limited to
          information that affects your performance of potential Jobs through
          our Site. All requests are conducted in accordance with applicable
          laws, including the FCRA. We use an outside agency for this screening:
          Yardstik, Inc., 100 Washington Ave S, Suite 600, Minneapolis, MN
          55401, Phone: 1-888-278-5042. As a result, Yardstik may obtain a
          Consumer Report on you as a user of the Site.
        </li>
        <li>
          Authorization and Release: Pursuant to the United Stated Fair Credit
          Reporting Act, you hereby authorize Spur and its designated agents and
          representatives to conduct a comprehensive review of your background
          through a Consumer Report and/or an investigative consumer report to
          be generated for pre-screening as a Worker authorized to use our Site
          and as may be required to qualify for certain Jobs through our Site.
          By accepting these Terms of Service, you acknowledge and agree that
          you have carefully read and understand this disclosure for the
          procurement of Consumer Reports. You also understand that information
          contained in your profile or otherwise disclosed by you before or
          during your use of our Site, if any, may be used for the purpose of
          obtaining Consumer Reports and/or investigative consumer reports. By
          accepting these Terms of Service, you authorize law enforcement
          agencies, learning institutions (including public and private schools
          and universities), information service bureaus, credit bureaus,
          record/data repositories, courts (federal, state, local, or
          international), motor vehicle records agencies, your past or present
          employers, the military, and other individuals and sources to furnish
          any and all information about you that is requested by the consumer
          reporting agency. You certify the information that you provide in
          order for Spur or its designated agents or representatives to obtain a
          Consumer Report is true and correct and will be valid for any reports
          that may be requested by or on behalf of Spur.
        </li>
      </ul>
      <p>
        You agree that certain Jobs may require that you satisfy additional
        background checks, in which case your application for such Job will be
        deemed to be your authorization to obtain any additional reports
        required by such Job.
      </p>
      <p>Payment for Jobs Completed:</p>
      <p>
        Once you have accepted a Job through the Site, you will be prompted to
        start and end the Job through the Site to record time worked. Once you
        end the Job, the Workplace has 24 hours to review the time entered. Any
        time that is deemed incorrect can be disputed by either the Workplace or
        you. If the time entered is not disputed by either party within 24
        hours, the Job is closed automatically, the Workplace will be
        responsible for Total Charges, and you will only be entitled to the
        amount of the agreed upon hourly rate multiplied by the hours recorded
        minus any withholdings applicable by law or contract. In limited cases,
        a Workplace may have a longer period of time for review, but only if set
        forth in a written agreement between the Workplace and Spur.
      </p>
      <p>
        After you have indicated your completion of the Job, and after the
        Workplace has rated the work and indicated its acceptance of your
        completion of such Job through the Site, funds shall be paid weekly via
        direct deposit to you in the amount agreed upon at acceptance of the
        Job.
      </p>
      <p>
        Enrollment in direct deposit is voluntary and is not a condition to hire
        or continued employment with Spur. Failure to enroll in direct deposit
        will not adversely the terms or conditions of your employment with Spur
        in any way. If you wish to enroll in direct deposit, you may select the
        financial institution that receives the deposit by timely completing the
        necessary information on the Site.
      </p>
      <h4>Ratings and Performance:</h4>
      <p>
        Workers are rated by the Workplace at the conclusion of a Job. The sole
        means of performance evaluation is in the rating system provided through
        the Site. We maintain the right to review performance or engage in any
        disciplinary action. Within a certain time frame after completing a Job,
        Workplaces can submit a rating (<strong>“Rating”</strong>) about you.
        Any Ratings reflect the opinion of the Workplace authorized user and do
        not reflect the opinion of Spur.
      </p>
      <p>
        Within a certain time frame after completing a Job, you can submit a
        rating (also a <strong>“Rating”</strong>) about the Workplace. Any
        Ratings reflect the opinion of the Worker and do not reflect the opinion
        of Spur. Ratings by Workers and Workplaces must be fair, truthful, and
        factual.
      </p>
      <p>
        Workers and Workplaces are prohibited from manipulating the Ratings
        system in any manner, such as instructing a Worker or Workplace to
        respond positively or negatively about another Worker or Workplace.
      </p>
      <p>
        You hereby waive any suit, claim, or action against Spur for defamation,
        slander, libel, or any other tort related to or arising out of Spur’s
        re-publication of any Rating, and hereby agree to indemnify, defend, and
        hold Spur harmless for any suit, claim, or action arising out of your
        Rating of any Workplace.
      </p>
      <h4>Additional Warranties and Representations:</h4>
      <p>
        You hereby warrant, represent, and agree that, upon each acceptance of a
        Job:
      </p>
      <ul>
        <li>
          You are capable of completing the Job in accordance with the Job
          requirements as posted on the Site;
        </li>
        <li>
          The skills, experience, background, and other information contained in
          your profile is accurate and not misleading;
        </li>
        <li>
          You have not exceeded the maximum work hours permitted by Spur (as
          described above) for that workday or work week.
        </li>
      </ul>
      <h4>Terms Specific to Workplaces:</h4>
      <p>
        If you are agreeing to these Terms of Service on behalf of a Workplace,
        you represent and warrant as follows:
      </p>
      <p>
        You represent that you have the authority to bind the Workplace (whether
        a person or legal entity) to the Terms of Service and, in such event,
        <strong>“you”</strong> and <strong>“your”</strong> will refer and apply
        to that person or legal entity that serves as the Workplace.
      </p>
      <p>
        You acknowledge and agree that the Site is intended for use only by
        Workplaces who can request and use temporary Workers through the Site.
      </p>
      <p>
        While you are responsible for deciding the requirements of a Job, for
        determining the price to be paid for a Job (subject to Spur’s
        requirements) and supervising the performance of the Worker during the
        Job, Spur will at all times be the employer of the Worker performing the
        Job. As such, we are solely responsible for hiring, firing, and
        disciplining the Worker, processing time entries and resulting
        compensation of the Worker, providing employee benefits (if any) to the
        Worker, providing periodic or ongoing training to Workers, and
        conducting the overall evaluation of the Worker for purposes of
        determining whether he or she may continue to use our Site. Spur is the
        sole entity responsible for receiving complaints from Workers regarding
        Jobs or otherwise and for notifying Workers of the termination or change
        of their assigned Job. Workers are required to comply with the Spur
        Employee Handbook and other policies and procedures of Spur, as adopted
        from time to time and made available on our Site.
      </p>
      <h4>Tax Reporting &amp; Payments:</h4>
      <p>
        The Workplace understands and agrees that Workers are employees of Spur.
        Spur will collect a Form W-4 from each Worker. Spur assumes
        responsibility for paying such wages to the Worker when due, and
        withholding and depositing all payroll taxes and other withholdings from
        such amounts as required by law.
      </p>
      <h4>Employee Benefits and Withholding:</h4>
      <p>
        As the employer, Spur has the sole discretion to determine whether any
        employee benefits will be offered to a Worker and the terms thereof. A
        Worker will not be entitled at any time to any benefits or perquisites
        offered by a Workplace. You agree not to advise nor represent to any
        Worker that such Worker shall be covered by or may participate in, any
        employee benefit plan maintained by you or your affiliates.
      </p>
      <h4>I-9 &amp; E-Verify:</h4>
      <p>
        As the employer, Spur verifies the identity and employment authorization
        of each Worker via Form I-9 and E-Verify.
      </p>
      <h4>Insurance:</h4>
      <p>
        Spur maintain appropriate general, umbrella, professional, crime,
        technology errors and omissions, and employment practices liability
        insurance. You may request a copy of Spur’s Certificate of Insurance by
        contacting Spur’s Legal Department at P.O. Box 107, Huntsville, Alabama
        35804 or by email at{' '}
        <a href="mailto:Legal@SpurJobs.com">Legal@SpurJobs.com</a>.
      </p>
      <p>
        We do not provide any insurance coverage to Workplaces, such as general
        commercial, professional, or employment practices liability insurance,
        or workers’ compensation insurance.
      </p>
      <h4>Our Relationship:</h4>
      <p>
        By accepting the Terms of Service, you acknowledge that the relationship
        between you and Spur is an independent contractor relationship. Nothing
        in these Terms of Service shall be deemed to create any form of
        partnership, principal-agent, employer-employee, or co-employment
        relationship, or joint venture, between you, Spur or the Worker.
      </p>
      <p>
        Spur is not a Professional Employer Organization as defined by the
        Internal Revenue Code and regulations promulgated thereunder.
      </p>
      <h4>Workers:</h4>
      <p>
        You acknowledge that Workers are employees of Spur for the purposes of
        all federal, state and local employment laws. You agree, however, to the
        terms of indemnification described below under “Indemnity,” which would
        include your indemnification for any violation of employment laws by you
        or your personnel or other agents.
      </p>
      <h4>OSHA Compliance:</h4>
      <p>
        You represent and warrant that the physical location of any Job will
        comply with all applicable Occupational Safety and Health Administration
        (OSHA) regulations in effect at the time the Worker performs the Job.
        Except where prohibited by applicable law or where agreed in a separate
        written agreement signed by an officer of Spur, you agree to indemnify,
        defend, and hold Spur harmless from suits, claims, or other expenses
        arising out of a violation or alleged violation of any OSHA regulation
        arising out of any Job or your use of the Site, including but not
        limited to Spur’s attorney’s fees, court costs, or fines imposed by a
        government body. You further agree to allow a representative of Spur to
        conduct an on-site review of the premises where a Job is to be performed
        if Spur decides, in its sole discretion, that such a review is necessary
        or advisable.
      </p>
      <h4>Job Acceptance:</h4>
      <p>
        You acknowledge that you are solely responsible for posting Jobs on the
        Site. Spur is not obligated to monitor the posting of Jobs, the source
        of Jobs, the number of Jobs or their frequency. The Workplace agrees
        that Workers control the acceptance of Jobs in their sole discretion.
        Spur does not guarantee any rate of acceptance of Jobs, that any
        particular Job will be accepted, or that any specific Worker will accept
        a Job, as acceptance of Jobs is fully dependent on the Workers. Spur
        does not guarantee that a Workplace’s jobs will be filled by Workers or
        any specific Worker on the Site.
      </p>
      <h4>Background Checks and Disclaimer of Warranty:</h4>
      <ul>
        <li>
          **Background Checks:** We or our designated agents or representatives
          conduct background checks on all Workers from a consumer reporting
          agency in connection with their request to use the Site for work
          purposes. You hereby acknowledge that Spur checks the backgrounds of
          each Worker using a third-party background check service that
          includes, but is not limited to, a sex offenders registry check,
          county, state, federal and national criminal records check, and
          domestic and international terrorist watch list check. You acknowledge
          that, to the extent that Spur or its designated agent or
          representative conducts a background check, it does so only upon the
          Worker’s initial hire by Spur. Additional background checks for
          specific Job types will be conducted pursuant to a separate agreement
          between you and Spur as part of that Job’s credential requirement.
        </li>
        <li>
          Spur has its own internal minimum standards for Workers, which we
          apply to background checks performed. Our internal standards are
          proprietary and sensitive and may change from time to time. Our
          standards are intended to comply with federal law and to ensure a
          certain minimal level of qualification under Spur’s own criteria.
          However, Spur makes no representation that the Workers who satisfy
          Spur’s standards for background checks have never committed a crime,
          been arrested, experienced credit problems, filed for bankruptcy, or
          otherwise engaged in activities that you may deem unconducive to the
          Job or your Workplace.
        </li>
      </ul>
      <h4>Payments to Workers:</h4>
      <ul>
        <li>
          Once a Worker has accepted a Job through the Site, the Worker will be
          prompted to start and end the Job on the Site to record time worked.
          After the Worker has completed the Job and indicated such completion,
          we will process and pay the Worker based on the Job fee agreed upon
          between the Worker and Workplace at acceptance of the Job. Payment
          will be made on a weekly basis.
        </li>
        <li>
          By accepting these Terms of Service, the Workplace agrees that the
          rate to be paid per hour by the Workplace, less our portion of such
          fees, will comply with all applicable federal, state and local minimum
          wage laws. This means that the minimum hourly compensation to be paid
          to a Worker (after deduction of our fees) by a Workplace shall be
          $7.25 per hour, unless applicable minimum wage laws require a higher
          hourly compensation, in which case the higher hourly compensation will
          be the minimum hourly compensation paid to a Worker (after deduction
          of our fees).
        </li>
      </ul>
      <h4>Hourly Rate, Payment Method, and Payment Processing Errors:</h4>
      <ul>
        <li>
          **Hourly Rate:** The Workplace sets the hourly pay rate for jobs
          booked through Spur, provided that, as described above, the wage paid
          to the Worker (after deduction of Spur’s fee) must satisfy our minimum
          rate requirements and the applicable federal, state and local minimum
          wage and other applicable laws. The Worker will start and end each Job
          via the Site. Once the Worker ends the Job, the Workplace has 24 hours
          to review the time entered. Any time that is deemed incorrect can be
          disputed by either the Workplace or the Worker. If the time entered is
          not disputed by either party within 24 hours, the Job is closed
          automatically, the Workplace will be responsible for Total Charges,
          and the Worker will only be entitled to the amount of the agreed upon
          hourly rate multiplied by the hours recorded minus taxes and Spur
          Admin costs and any withholdings applicable by law or contract. In
          limited cases, a Workplace may have a longer period of time for
          review, but only if set forth in a written agreement between the
          Workplace and Spur.
        </li>
        <li>
          **Extended Shift Fee:** You will be charged an extended shift fee for
          any shift in which the Worker works 0.5 hours past the scheduled time
          for such Job, as entered on the Site at the time the Job is posted.
          The extended shift fee is equivalent to 1.5 of the hourly rate for the
          Job (including Spur’s fee).
        </li>
        <li>
          **Payment Method:** You may elect to pay us either automatically by
          credit card or automated clearing house (ACH) payment, or we may
          approve you for invoicing.
        </li>
        <li>
          **Credit Card or ACH:** When you add or use a new credit card or ACH
          payment method, Spur may verify the payment method by authorizing a
          nominal amount, not to exceed one dollar ($1), or a similar sum in the
          payment method’s local currency. When you add a payment method during
          account creation, we will automatically save that payment method to
          Your Account so it can be used for a future transaction. Please note
          that payment methods involve the use of third-party payment service
          providers. Your payment method may be subject to additional terms and
          conditions imposed by the applicable third-party payment service
          provider. Please review these terms and conditions before using a
          payment method. You authorize the third-party to store your payment
          method information and charge your payment method as outlined in these
          payments terms. If your payment method’s account information changes
          (e.g., account number, routing number, expiration date) it is your
          responsibility to update the information in order to continue use of
          the Site. You are solely responsible for the accuracy and completeness
          of your payment method information. Spur is not responsible for any
          loss suffered by you as a result of incorrect payment method
          information provided by you.
        </li>
        <li>
          **Invoicing:** If you are approved by Spur for invoicing, we will bill
          you weekly at the Workplace email address on file. Invoices that are
          not paid within 15 days of issuance will accrue interest at a rate of
          1.5% per month (or, if applicable local, state, or federal law
          mandates a lower maximum rate, such maximum interest rate). A
          Workplace may have a different payment term, but only if set forth in
          a written agreement between the Workplace and Spur. We reserve the
          right, at our sole option and discretion, to permanently or
          temporarily remove your access to the Site if any invoices remain
          unpaid 15 days after issuance. We further reserve the right to assign
          any unpaid invoices to a collection agency. You hereby waive any and
          all exemptions and agree to pay all costs, attorneys’ fees and
          expenses incurred in connection with any collection efforts.
        </li>
        <li>
          **Payment Processing Errors:** We will take steps to rectify any
          payment processing errors that we become aware of. These steps may
          include crediting or debiting (as appropriate) the same payment method
          used for the original payment by you, so that you end up paying the
          correct amount.
        </li>
      </ul>
      <h4>Ratings and Performance:</h4>
      <p>
        Workers are rated by the Workplace at the conclusion of a Job. The sole
        means of performance evaluation is in the rating system provided through
        the Site. We maintain the right to review performance or engage in any
        disciplinary action with respect to any Worker. Within a certain time
        frame after completing a Job, Workplaces can submit a Rating about the
        Worker. Any Ratings reflect the opinion of the Workplace authorized user
        and do not reflect the opinion of Spur. Within a certain time frame
        after completing a Job, a Worker can submit a Rating about the
        Workplace. Any Ratings reflect the opinion of the Worker and do not
        reflect the opinion of Spur. Ratings by Workers and Workplaces must be
        fair, truthful, and factual.
      </p>
      <p>
        Workers and Workplaces are prohibited from manipulating the Ratings
        system in any manner, such as instructing a Worker or Workplace to
        respond positively or negatively about another Worker or Workplace.
      </p>
      <p>
        You hereby waive any suit, claim, or action against Spur for defamation,
        slander, libel, or any other tort related to or arising out of Spur’s
        re-publication of any Rating, and, except where prohibited by applicable
        law or otherwise agreed in writing signed by an officer of Spur and you,
        you hereby agree to indemnify, defend, and hold Spur harmless for any
        suit, claim, or action arising out of your Rating of any Worker.
      </p>
      <h4>Additional Warranties and Representations:</h4>
      <p>As the Workplace, you warrant, represent, and agree that:</p>
      <ul>
        <li>
          The Job as described on the Site is a fair and accurate presentation
          of the work that you wish to have performed by the Worker;
        </li>
        <li>
          You have provided any necessary descriptions, instructions, schedules,
          and specification to the Worker in the Job posting on the Site;
        </li>
        <li>
          Any descriptions of the form and scope of the desired Job are
          reasonably accurate;
        </li>
        <li>
          The pay rate specified in the Job acceptance is correct and acceptable
          to the Workplace;
        </li>
        <li>
          All Jobs posted by you comply with applicable federal, state, and
          local international law; and
        </li>
        <li>
          You will cooperate with us to comply with all legal requirements that
          apply to our employment relationship with the Workers, including but
          not limited to providing a reasonable accommodation under the
          Americans with Disabilities Act (ADA) and preventing unlawful
          harassment and cooperating in the investigation of and non-retaliation
          for any allegation of unlawful harassment.
        </li>
      </ul>
      <h3>User Information, User Generated Content, and Restrictions on Use</h3>
      <p>
        You acknowledge that Spur has the right and obligation to pre-screen or
        validate the User Information that Users of the Site submit in
        connection with establishing a profile or account with Spur or any
        content submitted to Spur or posted on our Site (
        <strong>“User Generated Content”</strong>). You are solely responsible
        for providing accurate, current and complete information in connection
        with any User Information or User Generated Content that you publish to
        the Site or make available through a Job, and you represent and warrant
        that you have all necessary rights to submit such User Information or
        User Generated Content to us.
      </p>
      <p>
        To market our Sites, Services, and mobile applications, we may use the
        information you provide in your profile, such as your name, picture, the
        date you created your account, your employment and/or education history,
        previous Jobs that you have completed or submitted, ratings of other
        users, and self-description in our marketing materials to third parties
        to whom you or your agents authorize us to disclose your personal
        information.
      </p>
      <p>
        You agree not to use this Site for any use other than the business
        purposes for which it was intended. You agree not to take any of the
        following actions with respect to the Site or any server providing
        hosting to the Site, User Information, or User Generated Content. You
        agree not to use the Site to upload, post, email, distribute, transmit,
        link, solicit, or otherwise make available any content or use the Site
        in any manner that:
      </p>
      <ul>
        <li>
          uploads or transmits any unsolicited advertising, promotional
          materials, “junk mail”, “spam”, “chain letters”, “pyramid schemes”, or
          any other form of solicitation, commercial or otherwise;
        </li>
        <li>
          decompiles, uses reverse engineering, disassembles, derives the source
          code of or decrypts the Site or server hosting the Site;
        </li>
        <li>
          manipulates or otherwise displays the Site by using framing, mirroring
          or similar navigational technology or directly links to any portion of
          the Site other than the main homepage located at{' '}
          <a href="https://spurstaffing.com/">www.spurstaffing.com;</a>
        </li>
        <li>
          uses any robot, spider, scraper or other automatic or manual means to
          access the Site or copies any content or information on the Site;
        </li>
        <li>
          removes, obscures, or alters any proprietary notices (including any
          notice of copyright or trademark) of Spur or its affiliates, partners,
          suppliers or the licensors;
        </li>
        <li>
          modifies, adapts, improves, enhances or makes any derivative work from
          the Site;
        </li>
        <li>
          disables, overburdens, impairs or otherwise interferes with or
          interrupts the Site or any hardware, software, system or network
          connected with the Site;
        </li>
        <li>
          probes, scans, or tests the vulnerability of or breach the
          authentication measures of the Site or any related networks or
          systems;
        </li>
        <li>
          interferes with any other party’s use and enjoyment of the Site;
        </li>
        <li>
          infringes the copyright, trademark or any proprietary rights or
          discloses a trade secret or confidential information in violation of a
          confidentiality or non-disclosure agreement;
        </li>
        <li>
          compiles, uses, downloads or otherwise copies any User Information or
          any portion thereof, or transmits, provides or otherwise distributes
          (whether or not for a fee) such information to any third party;
        </li>
        <li>
          is fraudulent, malicious or unlawful, unauthorized or contains
          defamatory or illegal information, images, materials or descriptions;
        </li>
        <li>promotes or provides instructions for illegal activities;</li>
        <li>
          encourages any conduct that would constitute a criminal offense or
          that gives rise to civil liability;
        </li>
        <li>
          disseminates viruses or other computer code, files or programs that
          interrupt, destroy or limit the functionality of any computer software
          or hardware;
        </li>
        <li>
          attempts to gain unauthorized access to any other accounts, computer
          systems or networks connected to any server or systems through
          hacking, password mining or any other means;
        </li>
        <li>
          accesses systems, data or information not intended by Spur to be made
          accessible to you;
        </li>
        <li>
          disseminates employee information (such as social security numbers)
          that would be prohibited by applicable law, if such dissemination had
          been done by Spur; or
        </li>
        <li>otherwise violates any applicable federal, state, or local law.</li>
      </ul>
      <h3>Privacy Policy</h3>
      <p>
        Your privacy is important to us. Please take a moment to review our
        Privacy Policy, which explains Spur’s practices relating to the
        collection and use of Your User Information through or in connection
        with the Site. The Privacy Policy is incorporated into this Terms of
        Service, and governs our use of your User Information and any other
        information you submit or otherwise make available to us in connection
        with the Site. Our Privacy Policy is available at{' '}
        <Link to="/privacy-policy">“Privacy Policy”</Link>.
      </p>
      <h3>No Warranties</h3>
      <p>
        ALL CONTENT ON THE SITE IS PROVIDED TO YOU ON AN “AS-IS,” “AS-AVAILABLE”
        BASIS AND SPUR EXPRESSLY DISCLAIMS ALL WARRANTIES AND CONDITIONS OF ANY
        KIND EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED
        WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
        NON-INFRINGEMENT. SPUR MAKES NO WARRANTY AS TO THE ACCURACY,
        COMPLETENESS, CURRENCY OR RELIABILITY OF ANY CONTENT AVAILABLE THROUGH
        THE SITE. YOU ARE RESPONSIBLE FOR VERIFYING ANY INFORMATION BEFORE
        RELYING ON IT. USE OF THE SITE IS AT YOUR SOLE RISK. SPUR MAKES NO
        REPRESENTATIONS OR WARRANTIES THAT USE OF THE SITE WILL BE
        UNINTERRUPTED, COMPLETELY SECURE OR ERROR-FREE. YOU ARE RESPONSIBLE FOR
        TAKING ALL PRECAUTIONS NECESSARY TO ENSURE THAT ANY CONTENT YOU MAY
        OBTAIN FROM THE SITE IS FREE OF VIRUSES, WORMS, OR OTHER “MALWARE.”
      </p>
      <h3>Trademarks</h3>
      <p>
        All trademarks, service marks and logos that are used or displayed on
        the Site are owned by Spur. You must obtain Spur’s written permission
        prior to using any trademark or service mark of Spur. Unauthorized use
        of any trademarks, service marks or logos used on the Site may be a
        violation of state, national and international trademark laws.
        Additionally, Spur’s custom icons, graphics, logos and scripting on the
        Site may be covered by trademark, trade dress, copyright or other
        proprietary right law, and may not be copied, modified or used, in whole
        or in part, without the prior written permission of Spur.
      </p>
      <h3>Copyright Infringement Notice</h3>
      <p>
        It is Spur’s policy to remove data or content that violates applicable
        copyright law. Pursuant to § 512 of Title 17 of the United States Code,
        Spur has implemented procedures for receiving written notification of
        claimed copyright infringement and for processing such claims in
        accordance with the law. To file a copyright infringement notification
        with us, you will need to send a written communication to Spur’s Legal
        Department, listed below, containing substantially the following
        information or such other information as may be required by 17 U.S.C. §
        512(c)(3):
      </p>
      <ul>
        <li>
          a physical or electronic signature of a person authorized to act on
          behalf of the owner of an exclusive right that is allegedly infringed;
        </li>
        <li>
          identification of the copyrighted work claimed to have been infringed,
          or, if multiple copyrighted works at a single online site are covered
          by a single notification, a representative list of such works at that
          site;
        </li>
        <li>
          identification of the material that is claimed to be infringing or to
          be the subject of infringing activity and that is to be removed or
          access to which is to be disabled, and information reasonably
          sufficient to permit the service provider to locate the material;
        </li>
        <li>
          information reasonably sufficient to permit the service provider to
          contact the complaining party, such as an address, telephone number,
          and, if available, an electronic mail address at which the complaining
          party may be contacted;
        </li>
        <li>
          a statement that the complaining party has a good faith belief that
          use of the material in the manner complained of is not authorized by
          the copyright owner, its agent, or the law; and
        </li>
        <li>
          a statement that the information in the notification is accurate, and
          under penalty of perjury, that the complaining party is authorized to
          act on behalf of the owner of an exclusive right that is allegedly
          infringed.
        </li>
      </ul>
      <p>
        Please direct any such notice to Spur’s Legal Department at P.O. Box
        107, Huntsville, Alabama 35804 or by email at{' '}
        <a href="mailto:legal@spurstaffing.com">legal@spurstaffing.com</a>.
      </p>
      <p>
        The foregoing contact information is for use solely with notice of
        copyright infringement and you may not receive a response to any other
        communications, feedback, comments, or requests for technical support
        delivered to the foregoing address or email.
      </p>
      <h3>Reviews, Comments, and Other Content</h3>
      <p>
        If you post any User Generated Content, including, but not limited to,
        reviews, comments, photos, statements, ideas, or questions to the Site,
        you acknowledge and agree that all such User Generated Content shall
        comply with the Terms of Service and you may not use any fake or “dummy”
        email address or otherwise impersonate any other person or entity, or
        otherwise mislead as to the origin of such User Generated Content.
      </p>
      <p>
        Unless we indicate otherwise, you grant Spur an unlimited, worldwide,
        perpetual, irrevocable, enterprise-wide, sub-licensable, transferable,
        and royalty-free license to use your User Generated Content on any media
        and in any form. If you provide any names or user names in connection
        with such User Generated Content, you grant Spur all right, title, and
        interest to that name or user name. You represent and warrant that all
        User Generated Content that you submit or post complies with any
        guidelines or rules of the United States Federal Trade Commission,
        including, but not limited to, 16 C.F.R. Part 255 regarding
        truth-in-advertising and disclosure requirements. You represent and
        warrant that all User Generated Content that you submit to the Site is
        accurate, truthful, and non-deceptive, and that you possess evidence to
        back up any and all claims made in such User Generated Content. You
        further represent and warrant that you own or otherwise have the legal
        authority the proprietary rights to the User Generated Content that you
        submit or post to the Site and that, except where prohibited by
        applicable law or otherwise agreed in writing signed by an officer of
        Spur and you, you will indemnify, defend, and hold Spur harmless for all
        claims and/or lawsuits resulting from your posting or submission of User
        Generated Content.
      </p>
      <h3>Modifications</h3>
      <p>
        Spur reserves the right to modify the Site and the rules and regulations
        governing its use, at any time, including, without limitation, these
        Terms of Service. Modifications will be posted on the Site and the “Last
        Updated” date at the top of this web page will be revised. You
        understand and agree that if you use this Site after the date on which
        the Terms of Service have changed, Spur will treat your use as
        acceptance of the updated Terms of Service. Spur may make changes in the
        services described in the Site at any time.
      </p>
      <h3>Violation of Rules and Regulations; Disclosure of Information</h3>
      <p>
        Spur reserves the right to seek all remedies available at law and in
        equity for violations of the rules and regulations set forth in these
        Terms of Service, including the right to block access from a particular
        Internet address to the Site. Spur may cooperate with legal authorities
        and/or third parties in the investigation of any suspected or alleged
        crime or civil wrong. Except as may be expressly limited by the Privacy
        Policy or otherwise agreed in writing signed by an officer of Spur and
        you, Spur reserves the right at all times to:
      </p>
      <ul>
        <li>
          disclose any information as Spur deems necessary to satisfy any
          applicable law, regulation, legal process or governmental request; or
        </li>
        <li>
          edit, refuse to post or to remove any information or materials, in
          whole or in part, as applicable, in Spur’s sole discretion.
        </li>
      </ul>
      <h3>Indemnity</h3>
      <p>
        In addition and cumulative to any indemnification obligations otherwise
        provided herein and except where prohibited by applicable law or
        otherwise agreed in writing signed by an officer of Spur and you, you
        agree to indemnify, hold harmless, and defend Spur and its officers,
        directors, employees, agents, affiliates, successors, and permitted
        assigns (collectively, <strong>“Indemnified Parties”</strong>) against
        any and all losses, damages, liabilities, deficiencies, claims, actions,
        judgments, settlements, interest, awards, penalties, fines, costs, or
        expenses of whatever kind, including attorneys’ fees, that are incurred
        by an Indemnified Party (collectively, <strong>“Losses”</strong>),
        arising out of or related to any third-party claim (including the claim
        of any other user) that arises out of your breach of any warranty,
        representation, or agreement contained in these Terms of Service by you
        or your officers, directors, employees, agents, affiliates, successors,
        or permitted assigns. Notwithstanding anything in these Terms of
        Service, an Indemnified Party may select its own legal counsel to
        represent its interests.
      </p>
      <h3>Limitation of Liability</h3>
      <p>
        YOU UNDERSTAND AND AGREE THAT SPUR WILL NOT BE LIABLE FOR ANY DIRECT,
        INDIRECT, INCIDENTAL, CONSEQUENTIAL OR SPECIAL DAMAGES, WHETHER BASED IN
        CONTRACT, TORT, STRICT LIABILITY OR OTHERWISE, ARISING OUT OF OR IN ANY
        WAY CONNECTED WITH ACCESS TO OR USE OF THE SITE OR THE PERFORMANCE OR
        CONDUCT OF THE WORKER OR WORKPLACE DURING A JOB, EVEN IF WE HAVE BEEN
        ADVISED OF THE POSSIBILITY OF SUCH DAMAGES, INCLUDING BUT NOT LIMITED
        TO: (A) ANY ACTION TAKEN IN CONNECTION WITH AN INVESTIGATION BY LAW
        ENFORCEMENT AUTHORITIES REGARDING YOUR OR ANY OTHER PARTY’S USE OF THE
        SITE; (B) ANY ACTION TAKEN IN CONNECTION WITH THE COPYRIGHT(S) OR OTHER
        INTELLECTUAL PROPERTY OWNERS; (C) ANY DAMAGE TO ANY USER’S COMPUTER,
        MOBILE DEVICE, OR OTHER EQUIPMENT OR TECHNOLOGY; (D) RELIANCE BY ANY
        PARTY ON ANY CONTENT OBTAINED THROUGH USE OF THIS SITE; (E) THE FAILURE
        OF ANY WORKER TO PERFORM ANY JOB TO THE WORKPLACE’S SATISFACTION; (F)
        THE FAILURE OF ANY WORKPLACE TO PAY ANY AGREED-UPON AMOUNT; OR (G)
        WHETHER CAUSED IN WHOLE OR IN PART BY NEGLIGENCE, ACTS OF GOD,
        TELECOMMUNICATIONS FAILURE, THEFT OR DESTRUCTION OF, OR UNAUTHORIZED
        ACCESS TO THE SITE, OR RELATED INFORMATION OR PROGRAMS, THAT ARISE IN
        CONNECTION WITH: (1) MISTAKES OR OMISSIONS IN, OR DELAYS IN TRANSMISSION
        OF, INFORMATION TO OR FROM THE USER; (2) INTERRUPTIONS IN
        TELECOMMUNICATIONS CONNECTIONS TO THE SITE; OR (3) VIRUSES.
      </p>
      <p>
        OUR TOTAL LIABILITY TO YOU FROM ALL CAUSES OF ACTION AND UNDER ALL
        THEORIES WILL BE LIMITED TO THE AMOUNT YOU HAVE PAID FOR THE USE OF THE
        SITE, IF ANY, AND IF YOU HAVE PAID NO AMOUNT, THEN $50. YOU ALSO
        ACKNOWLEDGE AND AGREE THAT YOU HAVE VIEWED OR USED THE SITE WITH A FULL
        UNDERSTANDING OF THE LIMITATION OF OUR LIABILITY IN THESE TERMS OF
        SERVICE. BY ACCESSING THE SITE, YOU UNDERSTAND THAT YOU MAY BE WAIVING
        RIGHTS WITH RESPECT TO CLAIMS THAT ARE AT THIS TIME UNKNOWN OR
        UNSUSPECTED, AND IN ACCORDANCE WITH SUCH WAIVER, YOU ACKNOWLEDGE THAT
        YOU HAVE READ AND UNDERSTAND, AND HEREBY EXPRESSLY WAIVE, THE BENEFITS
        OF SECTION 1542 OF THE CIVIL CODE OF CALIFORNIA, AND ANY SIMILAR LAW OF
        ANY STATE OR TERRITORY, WHICH PROVIDES AS FOLLOWS: “A GENERAL RELEASE
        DOES NOT EXTEND TO CLAIMS WHICH THE CREDITOR DOES NOT KNOW OR SUSPECT TO
        EXIST IN HIS FAVOR AT THE TIME OF EXECUTING THE RELEASE, WHICH IF KNOWN
        BY HIM MUST HAVE MATERIALLY AFFECTED HIS SETTLEMENT WITH THE DEBTOR.”
      </p>
      <p>
        Where applicable law does not allow exclusions of liability for certain
        types of damages or where otherwise agreed in writing by an officer of
        Spur and you, some of the above limitations may not apply to you. In
        such cases, Spur’s liability will be limited to the fullest extent
        permitted by applicable law and any such written agreement.
      </p>
      <h3>Third Party Sites</h3>
      <p>
        Some links in the Site or Site may navigate you away from Spur’s Site
        and/or Site or redirect you to other websites, including websites
        operated by third parties. Please be aware that those websites may
        collect personally identifiable information (“PII”) from or about you
        and non-PII about your visit. The linked sites are not under the control
        of Spur, and the content available on the Internet sites linked to this
        Site do not necessarily reflect the opinion of Spur. Spur is providing
        these links to other Internet sites as a convenience to you, and access
        to any other Internet sites linked to this Site is at your own risk. The
        inclusion of any link does not imply a recommendation or endorsement by
        Spur of the linked site. You should review the terms of use and privacy
        policies that are posted on any website that you visit, before using any
        linked websites. Spur accepts no liability for any information,
        products, advertisements, content, services or software accessible
        through these third-party websites or for any action you may take as a
        result of linking to any such website. Spur is under no obligation to
        maintain any link on the Site and may remove a link at any time in its
        sole discretion for any reason whatsoever. Spur shall not be responsible
        or liable, directly or indirectly, for any damages or losses caused or
        alleged to be caused by or in connection with the use of or reliance on
        such content, products, services or other materials available on or
        through any such website. Spur is not responsible for the privacy
        practices of any other websites.
      </p>
      <h3>No Fiduciary Relationship</h3>
      <p>
        Except to the extent set forth in writing signed by an officer of Spur
        and you, there is no fiduciary relationship between you and us. The
        Terms do not create any relationship of principal and agent,
        partnership, joint venture, or in the case of a Worksite
        employer-employee, between you and us. You may not enter into any
        contract on our behalf or bind us in any way.
      </p>
      <h3>Right to Monitor</h3>
      <p>
        Spur reserves the right to actively monitor the use of the Site and Site
        and use any information gathered during such monitoring for any
        permissible purpose under the Privacy Policy. Additionally, Spur may, at
        any time as it deems appropriate, remove any materials from the Site
        that, in Spur’s sole discretion, may be illegal, may subject Spur to
        liability, may violate these Terms of Service, or are, in the sole
        discretion of Spur, inconsistent with Spur’s purpose for the Site.
      </p>
      <h3>Electronic Communications</h3>
      <p>
        When you visit the Site or Site or send emails to us, you are
        communicating with us electronically. You consent to receive
        communications from us electronically. We will communicate with you by
        email, sending you messages through the Site, or by posting notices on
        the Site. You agree that all agreements, notices, disclosures and other
        communications that we provide to you electronically satisfy any legal
        requirement that such communications be in writing. You further agree
        that any notices provided by us electronically are deemed to be given
        and received on the date we transmit any such electronic communication
        as described in these Terms of Service.
      </p>
      <h3>Governing Law and General Principles</h3>
      <p>
        This Site is operated by Spur from its offices within the United States
        of America (“U.S.”). Spur makes no representation that the information
        in the Site or the Site is appropriate or available for use in other
        non-U.S. locations, and access to the Site from territories where the
        contents of the Site may be illegal is prohibited. Those who choose to
        access this Site or the Site from other non-U.S. locations do so on
        their own initiative and are responsible for compliance with applicable
        local laws.
      </p>
      <p>
        By using the Site, regardless of where you live or are located in the
        world, you consent to these Terms of Service and any claims relating to
        the information, services or products made available through this Site
        or, in the case of a Worker, your employment by us, will be governed by
        the laws of the State of Alabama, U.S., excluding the application of its
        conflicts of law rules.
      </p>
      <p>
        If any provision of these Terms of Service shall be deemed unlawful,
        void, or for any reason unenforceable, then that provision will be
        deemed severable from these Terms of Service and will not affect the
        validity and enforceability of any remaining provisions.
      </p>
      <p>
        You may not assign, transfer or delegate these Terms of Service and your
        rights and obligations hereunder without Spur’s prior written consent.
        Spur may, without restriction assign, transfer or delegate these Terms
        of Service and any rights and obligations hereunder, at its sole
        discretion, with 30 days prior notice. Your right to terminate these
        Terms of Service at any time remains unaffected.
      </p>
      <p>
        Spur’s failure to enforce the strict performance of any provision of
        these Terms of Service will not constitute a waiver of Spur’s right to
        subsequently enforce such provision or any other provisions of these
        Terms of Service.
      </p>
      <p>
        You agree that any claim you may have arising out of or related to your
        use of the Site, or your relationship with Spur, must be filed within
        one year after such claim arose; otherwise, your claim is permanently
        barred. You agree that any violation, or threatened violation, by you of
        these Terms of Service constitutes an unlawful and unfair business
        practice that will cause us irreparable and unquantifiable harm. You
        also agree that monetary damages would be inadequate for such harm and
        consent to our obtaining any injunctive or equitable relief that we deem
        necessary or appropriate. These remedies are in addition to any other
        remedies we may have at law or in equity.
      </p>
      <p>
        Any notices or other communications to Workers and/or Workplaces
        permitted or required under these Terms of Service will be in writing
        and given by Spur via Site notifications, except to the extent that
        another method is required by law or agreed in a writing signed by an
        officer of Spur and you.
      </p>
      <p>
        Except as they may be supplemented by additional terms and conditions,
        policies, guidelines or standards, these Terms of Service constitute the
        entire agreement between Spur and you pertaining to the subject matter
        hereof, and supersede any and all prior oral or written understandings
        or agreements between Spur and you in relation to the access to and use
        of the Site (except to the extent that a separate written agreement
        provides otherwise and is both signed by an officer of Spur and you and
        specifically provides that it supersedes any conflicting terms in these
        Terms of Service).
      </p>
      <h3>Arbitration and Class Action</h3>
      <p>
        Any dispute, controversy, or claim arising out of, relating to, or in
        connection with your use of the Site, the contents of the Site, these
        Terms of Service, or in the case of a Worker your employment by us,
        including the breach, termination, or validity thereof, shall be finally
        resolved by binding arbitration before the American Arbitration
        Association using its Commercial Arbitration Rules (except where
        prohibited by law). The site of such arbitration shall be Huntsville,
        Alabama. The arbitrator shall have the power to rule on any challenge to
        its own jurisdiction or to the validity or enforceability of any portion
        of the agreement to arbitrate. To the maximum extent permitted by law,
        you agree to arbitrate solely on an individual basis, and acknowledge
        and agree that these Terms of Service do not permit class arbitration or
        any claims brought as a plaintiff or class member in any class,
        representative, or collective arbitration proceeding. To the maximum
        extent permitted by law, you agree that the arbitral tribunal may not
        consolidate more than one person’s claims, and may not otherwise preside
        over any form of a representative or class proceeding. You agree that
        notwithstanding the tribunal’s power to rule on its own jurisdiction and
        the validity or enforceability of the agreement to arbitrate, the
        tribunal has no power to rule on the validity or enforceability of the
        agreement to arbitrate solely on an individual basis. You agree that in
        the event the prohibition on class arbitration is deemed invalid or
        unenforceable by a court of competent jurisdiction, then the remaining
        portions of this arbitration agreement will remain in full force and
        effect. Judgment on any award may be entered by any court having
        competent jurisdiction, and you specifically agree and waive objection
        and hereby submit to the jurisdiction of the state and federal courts in
        Madison County, Alabama. The provisions of this paragraph
        notwithstanding, you agree that we may seek temporary or preliminary
        injunctive relief against you prior to arbitrating any dispute,
        controversy, or claim as necessary to protect our interests in the
        enforcement of these Terms of Service without waiving the arbitration
        provisions of these Terms of Service.
      </p>
      <h3>WAIVER OF JURY TRIAL</h3>
      <p>
        SEPARATE AND APART FROM THE FOREGOING ARBITRATION PROVISION, AND IN
        ADDITION TO SUCH PROVISION, YOU IRREVOCABLY AND UNCONDITIONALLY WAIVE,
        TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, ANY RIGHT YOU HAVE TO
        A TRIAL BY JURY IN ANY LEGAL ACTION, PROCEEDING, CAUSE OF ACTION, OR
        COUNTERCLAIM ARISING OUT OF OR RELATING TO THESE TERMS OF SERVICE, THE
        TRANSACTIONS CONTEMPLATED HEREIN, OR IN THE CASE OF A WORKER YOUR
        EMPLOYMENT BY SPUR. YOU CERTIFY AND ACKNOWLEDGE THAT YOU HAVE CONSIDERED
        THE IMPLICATIONS OF THIS WAIVER, YOU HAVE MADE THIS WAIVER KNOWINGLY AND
        VOLUNTARILY, AND THAT WE WERE INDUCED TO ENTER INTO THESE TERMS OF
        SERVICE BY, AMONG OTHER THINGS, THE WAIVER AND CERTIFICATIONS IN THIS
        PARAGRAPH.
      </p>
      <h3>Jurisdiction and Venue</h3>
      <p>
        SEPARATE AND APART FROM THE FOREGOING ARBITRATION PROVISION, AND IN
        ADDITION TO SUCH PROVISION, you agree that any legal suit, action, or
        proceeding arising out of or relating to these Terms of Service, the
        transactions contemplated hereby, or in the case of a Worker your
        employment by Spur, that for any reason is not submitted to arbitration
        (or in the event the arbitration provision is stricken from this
        agreement) shall only be instituted in the state or federal courts
        located in Madison County, Alabama, and you irrevocably submit to the
        exclusive jurisdiction of such courts in any suit, action, or proceeding
        not covered by the arbitration provision set out above. The provisions
        of this paragraph do not alter, amend, or supersede the requirement that
        you submit any dispute, controversy, or claim arising out of or related
        to these Terms of Service, the transactions contemplated herein, or in
        the case of a Worker your employment by Spur, to binding arbitration as
        spelled out above.
      </p>
    </LegalDocument>
  );
}

export default TermsEn;
